import * as React from "react";
import { Check, Copy } from "./icons";

import { cn } from "utils/cn";
import { IconButton, type IconButtonProps } from "./IconButton";
import { notify } from "./Toast";

const COPIED_MS = 1500;

export interface CopyButtonProps extends Omit<IconButtonProps, "icon" | "label" | "size"> {
  /** Text written to the clipboard. */
  value: string;
  /** Accessible name and tooltip, e.g. "Copy public key". */
  label?: string;
  size?: IconButtonProps["size"];
  onCopied?: () => void;
}

/** Copies `value` and swaps to a check mark for a moment. Needs a secure context (https/localhost). */
export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ value, label = "Copy", size = "sm", onCopied, onClick, className, ...props }, ref) => {
    const [copied, setCopied] = React.useState(false);
    const timer = React.useRef<number | undefined>(undefined);

    React.useEffect(() => () => window.clearTimeout(timer.current), []);

    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) {
        return;
      }
      try {
        if (!navigator.clipboard) {
          throw new Error("Clipboard API unavailable");
        }
        await navigator.clipboard.writeText(value);
        window.clearTimeout(timer.current);
        setCopied(true);
        timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
        onCopied?.();
      } catch {
        notify.error("Couldn't copy to the clipboard");
      }
    };

    return (
      <>
        <IconButton
          ref={ref}
          data-ui="copy-button"
          label={copied ? "Copied" : label}
          size={size}
          icon={copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          className={cn(className, copied && "text-success-text hover:text-success-text")}
          onClick={handleClick}
          {...props}
        />
        <span role="status" className="sr-only">
          {copied ? "Copied to clipboard" : ""}
        </span>
      </>
    );
  }
);
CopyButton.displayName = "CopyButton";
